import { useContext } from 'react';
import { groupTimesByServiceWindow } from '@shared/availability/availabilityUtil';
import { AvailableTimes } from '@shared/availability/AvailableTimes';
import { RestaurantStatus } from '@shared/types/restaurants';
import { useAvailabilityContext } from 'webReservations/context/AvailabilityContext';
import { useRestaurantContext } from 'webReservations/context/RestaurantContext';
import { useFeatureFlagContext } from 'webReservations/featureFlags';
import { NoTimes } from 'webReservations/filters/NoTimes';
import { ComingSoon } from './ComingSoon';
import styles from './AvailableTimesContainer.scss';
import { useTableSelectedPresentation } from '../hooks/useTableSelectedPresentation';
import { GuestPricingBrowseContext } from 'webReservations/pricingAnalytics/GuestPricingBrowseContext';
import { useGuestPricingImpressionRecorder } from 'webReservations/pricingAnalytics/useGuestPricingImpressionRecorder';
import { TableSelectedDetails } from './TableSelectedDetails';
import {
  sendGa4AvailabilityFilterTimeApplyEvent,
  sendGa4AvailabilityFilterTimeNotAvailable,
  sendGa4AvailabilityFilterTimeStartEvent,
} from './gaHelpers';

export const AvailableTimesContainer = () => {
  const {
    restaurantDetails: {
      name: restaurantName,
      status: restaurantStatus,
      id: restaurantId,
    },
  } = useRestaurantContext();

  const {
    filteredAvailabilitiesGroupedByTime,
    isAvailabilitiesFetchLoading,
    selectedFloorPlanListingIds,
    setSelectedTime,
    hasNoAvailableTimesForSelectedGuestCount,
  } = useAvailabilityContext();
  const guestPricingBrowse = useContext(GuestPricingBrowseContext);
  const setServiceWindowIndex =
    guestPricingBrowse?.setServiceWindowIndex ?? (() => undefined);
  const { record } = useGuestPricingImpressionRecorder(restaurantId);
  const { isEnabled } = useFeatureFlagContext();
  const showNewFloorPlanTableFeatures = isEnabled('newFloorPlanTableFeatures');
  const { selectedTablePublicName } = useTableSelectedPresentation();

  const tablePublicName = showNewFloorPlanTableFeatures
    ? selectedTablePublicName
    : undefined;

  if (isAvailabilitiesFetchLoading) return null;

  if (restaurantStatus === RestaurantStatus.ComingSoon) {
    return <ComingSoon />;
  }

  if (hasNoAvailableTimesForSelectedGuestCount) {
    sendGa4AvailabilityFilterTimeNotAvailable({
      restaurantName,
    });

    return <NoTimes />;
  }

  sendGa4AvailabilityFilterTimeStartEvent({
    restaurantName,
  });

  const availableTimesGroupedByServiceWindow = groupTimesByServiceWindow(
    filteredAvailabilitiesGroupedByTime,
  );

  const serviceWindows = availableTimesGroupedByServiceWindow.map(
    ([serviceWindow, _times]: [string, string[]]) => serviceWindow,
  );

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    record('time_slot_selected', { selectedTimeOverride: time });
    sendGa4AvailabilityFilterTimeApplyEvent({
      restaurantName,
    });
  };

  const handleServiceWindowTabChange = (tabIndex: number) => {
    setServiceWindowIndex(tabIndex);
    record('service_window_selected', {
      serviceWindowIndexOverride: tabIndex,
    });
  };

  const showTableSelectedDetails =
    showNewFloorPlanTableFeatures && selectedFloorPlanListingIds.length === 1;

  return (
    <div className={styles.wrapper}>
      {tablePublicName ? (
        <p
          className={styles.tablePublicNameTop}
          data-testid="availability-selected-table-name"
        >
          {tablePublicName}
        </p>
      ) : null}
      {showTableSelectedDetails ? <TableSelectedDetails /> : null}
      <AvailableTimes
        {...{
          filteredAvailabilitiesGroupedByTime,
          serviceWindows,
          availableTimesGroupedByServiceWindow,
          onTimeSelect: handleTimeSelect,
          onServiceWindowTabChange: handleServiceWindowTabChange,
        }}
      />
    </div>
  );
};
